import {
  Avatar,
  Button,
  Empty,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { TableProps } from 'antd';
import {
  ImportOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { setupApi } from '@/api/setup';
import { studentsApi, type StudentStatus, type StudentSummary } from '@/api/students';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { fileUrl } from '@/shared/lib/file-url';

/** Initials from first + last name. Falls back to "?" for blank records. */
function initialsOf(s: StudentSummary): string {
  const a = (s.firstName || '').trim().charAt(0);
  const b = (s.lastName || '').trim().charAt(0);
  const out = (a + b).toUpperCase();
  return out || '?';
}

const STATUS_LABEL: Record<StudentStatus, string> = {
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  LEFT: 'Left',
  GRADUATED: 'Graduated',
};
const STATUS_COLOR: Record<StudentStatus, string> = {
  ACTIVE: 'success',
  SUSPENDED: 'warning',
  LEFT: 'default',
  GRADUATED: 'processing',
};

export function StudentsListPage() {
  const navigate = useNavigate();
  const yearId = useAuthStore((s) => s.academicYearId);
  const canCreate = useAuthStore((s) => s.hasAuthority('STUDENT_MGMT:CREATE'));
  const isSuperAdmin = useAuthStore((s) => s.hasAuthority('ROLE_SUPER_ADMIN'));

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState<StudentStatus | 'ALL'>('ACTIVE');
  const [classSectionId, setClassSectionId] = useState<number | undefined>();
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(0);
  }, [debounced, status, classSectionId, yearId]);

  const params = {
    q: debounced || undefined,
    classSectionId,
    academicYearId: yearId ?? undefined,
    status: status === 'ALL' ? undefined : status,
    page,
    size,
  };

  const { data, isFetching, refetch, dataUpdatedAt } = useQuery({
    queryKey: ['students', 'search', params],
    queryFn: () => studentsApi.search(params),
    placeholderData: (prev) => prev,
  });

  const { data: classSections } = useQuery({
    queryKey: ['setup', 'class-sections', yearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(yearId ?? undefined),
    enabled: yearId != null,
    staleTime: 5 * 60 * 1000,
  });

  const columns: TableProps<StudentSummary>['columns'] = [
    {
      // Lightweight identity column — photo if uploaded, initials otherwise.
      // Width is fixed so it doesn't fight the long-name column for space.
      title: '',
      key: 'avatar',
      width: 56,
      align: 'center',
      render: (_: unknown, row) => (
        <Avatar
          src={row.photoPath ? fileUrl(row.photoPath) : undefined}
          icon={<UserOutlined />}
          size={36}
          style={{
            background: row.photoPath ? undefined : 'var(--ant-color-fill-secondary)',
            color: 'var(--ant-color-text-secondary)',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {row.photoPath ? null : initialsOf(row)}
        </Avatar>
      ),
    },
    {
      title: 'Reg #',
      dataIndex: 'registrationNumber',
      width: 160,
      render: (rn: string, row) => (
        <Space orientation="vertical" size={0}>
          <Typography.Text code>{rn}</Typography.Text>
          {row.legacyAdmissionNumber && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Old: {row.legacyAdmissionNumber}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'firstName',
      render: (_: string, row) => (
        <Typography.Text strong>
          {row.firstName} {row.lastName}
        </Typography.Text>
      ),
    },
    {
      title: 'Class',
      dataIndex: 'currentClassName',
      width: 160,
      render: (cn: string | null, row) =>
        cn ? (
          <Tag>{cn}{row.currentSectionName ? ` · ${row.currentSectionName}` : ''}</Tag>
        ) : (
          <Typography.Text type="secondary">Not enrolled</Typography.Text>
        ),
    },
    {
      title: 'Primary guardian',
      dataIndex: 'primaryGuardianName',
      render: (name: string | null, row) =>
        name ? (
          <Space orientation="vertical" size={0}>
            <span>{name}</span>
            {row.primaryGuardianPhone && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.primaryGuardianPhone}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Status',
      dataIndex: 'currentStatus',
      width: 120,
      render: (s: StudentStatus) => (
        <Tag color={STATUS_COLOR[s]}>{STATUS_LABEL[s]}</Tag>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module"
        title="Students"
        description="On-roll students, current enrollment, and basic guardian context."
        count={data ? { value: data.totalElements, label: 'matching' } : undefined}
        onRefresh={() => refetch()}
        refreshKey={['students', 'search']}
        updatedAt={dataUpdatedAt}
        actions={
          canCreate ? (
            <Space>
              {isSuperAdmin && (
                <Button icon={<ImportOutlined />} onClick={() => navigate('/students/import')}>
                  Bulk import
                </Button>
              )}
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/students/new')}>
                New student
              </Button>
            </Space>
          ) : null
        }
      />

      <Space wrap size={12} style={{ rowGap: 12 }}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search by name or reg #"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 280 }}
        />
        <Select
          value={status}
          onChange={setStatus}
          options={[
            { value: 'ALL', label: 'All statuses' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'SUSPENDED', label: 'Suspended' },
            { value: 'LEFT', label: 'Left' },
            { value: 'GRADUATED', label: 'Graduated' },
          ]}
          style={{ minWidth: 160 }}
        />
        <Select
          placeholder="Any class · section"
          allowClear
          value={classSectionId}
          onChange={setClassSectionId}
          options={(classSections ?? [])
            .slice()
            .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
            .map((cs) => ({
              value: cs.id,
              label: `${cs.className} · ${cs.sectionName}`,
            }))}
          style={{ minWidth: 220 }}
          showSearch
          optionFilterProp="label"
        />
      </Space>

      {data && data.content.length === 0 && !isFetching ? (
        <Empty description="No students match the current filters" />
      ) : (
        <Table
          rowKey="id"
          size="middle"
          loading={isFetching}
          columns={columns}
          dataSource={data?.content ?? []}
          pagination={{
            current: (data?.number ?? page) + 1,
            pageSize: data?.size ?? size,
            total: data?.totalElements ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [10, 25, 50, 100],
            onChange: (next, nextSize) => {
              setPage(next - 1);
              if (nextSize !== size) setSize(nextSize);
            },
          }}
          onRow={(row) => ({
            onClick: () => navigate(`/students/${row.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      )}
    </div>
  );
}
